import React, { useMemo, useEffect, useCallback } from "react";
import { CheckOutlined } from "@ant-design/icons";
import { Alert, Form, Select, Tag } from "antd";
import { connect } from "dva";
import { CompanyStatus, Drivability } from "@/utils/enum";
import { getDrivers } from "@/services/associatedUsers";
import EntityFlagTag from "@/components/EntityFlagTag";
import SelectAutoComplete from "@/components/SelectAutocomplete";

const { Option } = Select;

const CompanyAndUserInputForm = ({
  // Redux
  configPerm,

  // Parent
  shipmentsList,
  entityAndUserFormRef,

  selectedTransporterEntityId,
  setSelectedTransporterEntityId,

  selectedTransporterUserId,
  setSelectedTransporterUserId,

  transporterUsersList,
  setTransporterUsersList,

  transporterEntitiesList,

  fetchTransporters,
  loadingAllTransporterEntities,
  loadNewTransporterUsersList,
  loadingTransporterUsers,
}) => {
  const handleSelectTransporterEntity = newEntityId => {
    setSelectedTransporterEntityId(newEntityId);
    loadNewTransporterUsersList(newEntityId);
  };

  const handleSelectTransporterUser = newUserId => {
    setSelectedTransporterUserId(newUserId);
  };

  const renderEntitySelect = useMemo(() => {
    return (
      <SelectAutoComplete
        placeholder="Select Company"
        value={selectedTransporterEntityId}
        onChange={handleSelectTransporterEntity}
        options={transporterEntitiesList}
        fetchOptions={fetchTransporters}
        style={{
          width: "100%",
          minWidth: 400,
        }}
        defaultActiveFirstOption={transporterEntitiesList.length === 1}
        isLoading={loadingAllTransporterEntities}
        renderOption={t => {
          const disabled = [CompanyStatus.Duplicate, CompanyStatus.Block].includes(t.status);

          if (configPerm["CoreService.AdminShipments.Accept"]) {
            return (
              <Option disabled={disabled} value={t.id} key={t.name + t.id} label={t.name}>
                <span>
                  {t.name}
                  <EntityFlagTag flagStatus={t.status} />
                </span>
              </Option>
            );
          }

          return (
            <Option value={t.id} key={t.id} disabled={!t.isElegible}>
              {t.name}
            </Option>
          );
        }}
      />
    );
  }, [
    selectedTransporterEntityId,
    transporterEntitiesList,
    loadingAllTransporterEntities,
    fetchTransporters,
    handleSelectTransporterEntity,
  ]);

  const fetchDrivers = useCallback(
    searchText =>
      getDrivers({
        page: 1,
        pageSize: 10,
        filter: searchText,
        filterId: selectedTransporterEntityId,
        shipmentIds: shipmentsList?.map(ship => ship.id),
      }).then(response => {
        setTransporterUsersList(response?.items ?? []);
      }),
    [selectedTransporterEntityId, shipmentsList],
  );

  const displayUserSelect = () => {
    return (
      <SelectAutoComplete
        placeholder="Select Driver"
        value={selectedTransporterUserId}
        onChange={handleSelectTransporterUser}
        options={transporterUsersList}
        fetchOptions={fetchDrivers}
        parseFetchedOptions={async response => ({
          totalCount: response?.totalCount ?? 0,
          items: response?.items ?? [],
        })}
        style={{
          width: "100%",
          minWidth: 400,
        }}
        defaultActiveFirstOption={transporterUsersList.length === 1}
        isLoading={loadingTransporterUsers}
        renderOption={u => {
          if (configPerm["CoreService.AdminShipments.Accept"]) {
            return (
              <Option value={u.userId} key={`${u.firstName} ${u.lastName} ${u.userId}`}>
                {`${`${u.firstName} ${u.lastName}`} - (${u.email})`}
                {!u.isElegible ? (
                  <Tag color="red" style={{ marginLeft: 5 }}>
                    Not Eligible
                  </Tag>
                ) : (
                  <CheckOutlined
                    style={{
                      fontSize: 14,
                      color: "#52c41a",
                      marginLeft: 5,
                    }}
                  />
                )}
              </Option>
            );
          }

          return (
            <Option value={u.userId} key={`${u.firstName} ${u.lastName} ${u.userId}`} disabled={!u.isElegible}>
              {`${`${u.firstName} ${u.lastName}`} - (${u.email})`}
            </Option>
          );
        }}
        disabled={transporterEntitiesList?.length === 0 || transporterUsersList?.length === 0}
      />
    );
  };

  const shipmentsRequireTWIC = shipmentsList.some(s => s.requiresTWIC);
  const shipmentsRequireEModal = shipmentsList.some(s => s.requiresEModal);
  const shipmentsRequireLotExit = shipmentsList.some(s => s.requiresLotExit);
  const shipmentHasInopVehicles = shipmentsList.some(s =>
    s.vehicles.some(v => v.vehicle?.drivabilityType === Drivability.Inoperational),
  );
  const shipmentHasEnclosedVehicles = shipmentsList.some(s => s.vehicles.some(v => v.enclosed));

  useEffect(() => {
    if (selectedTransporterEntityId) {
      entityAndUserFormRef.current.setFieldsValue({
        entityId: selectedTransporterEntityId,
      });
    }
  }, [selectedTransporterEntityId]);

  useEffect(() => {
    if (selectedTransporterUserId) {
      entityAndUserFormRef.current.setFieldsValue({
        userId: selectedTransporterUserId,
      });
    }
  }, [selectedTransporterUserId]);

  return (
    <Form ref={entityAndUserFormRef} layout="vertical">
      <Form.Item
        name="entityId"
        label="Company"
        style={{ marginLeft: "auto", marginRight: "auto", width: "80%" }}
        rules={[{ required: true, message: "Please select a company" }]}
      >
        {renderEntitySelect}
      </Form.Item>
      <Form.Item
        name="userId"
        label="Driver"
        style={{ marginLeft: "auto", marginRight: "auto", width: "80%" }}
        rules={[{ required: true, message: "Please select a driver" }]}
      >
        {displayUserSelect()}
      </Form.Item>
      {(shipmentsRequireTWIC || shipmentsRequireEModal) && (
        <Alert
          message={
            shipmentsRequireTWIC && shipmentsRequireEModal
              ? "TWIC Card and eModal Required"
              : shipmentsRequireTWIC
              ? "TWIC Card Required"
              : shipmentsRequireEModal
              ? "eModal Required"
              : ""
          }
          description={
            shipmentsRequireTWIC && shipmentsRequireEModal
              ? "One or more of the selected shipments requires a TWIC card and eModal credential for pickup. Only users with verified TWIC cards and eModal credentials under the selected company will be available to select."
              : shipmentsRequireTWIC
              ? "One or more of the selected shipments requires a TWIC card for pickup. Only users with verified TWIC cards under the selected company will be available to select."
              : shipmentsRequireEModal
              ? "One or more of the selected shipments requires eModal credential for pickup. Only users with verified eModal credentials under the selected company will be available to select."
              : ""
          }
          type="info"
          showIcon
          style={{
            marginLeft: "auto",
            marginRight: "auto",
            width: "80%",
            marginTop: 16,
          }}
        />
      )}
      {shipmentsRequireLotExit && (
        <Alert
          message="Lot Exit Requirement"
          description="One or more of the selected shipments requires lot exit. Please be sure that the driver selected for dispatch is familiar with the lot exit process. For more information about this process please call Autosled support."
          type="warning"
          showIcon
          style={{
            marginLeft: "auto",
            marginRight: "auto",
            width: "80%",
            marginTop: 16,
          }}
        />
      )}
      {shipmentHasEnclosedVehicles && (
        <Alert
          message="Enclosed Trailer Requirement"
          description="One or more of the selected shipments requires an enclosed trailer. Please be sure that the driver selected for dispatch has an enclosed trailer."
          type="warning"
          showIcon
          style={{
            marginLeft: "auto",
            marginRight: "auto",
            width: "80%",
            marginTop: 16,
          }}
        />
      )}
      {shipmentHasInopVehicles && (
        <Alert
          message="Inoperational Units"
          description="One or more of the selected shipments has inoperational units. Please be sure that the driver selected for dispatch will be able to move these units."
          type="warning"
          showIcon
          style={{
            marginLeft: "auto",
            marginRight: "auto",
            width: "80%",
            marginTop: 16,
          }}
        />
      )}
    </Form>
  );
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
}))(CompanyAndUserInputForm);
